(function () {
  'use strict';

  // Tracks active RAF loops per element. WeakMap allows GC when element is removed.
  const countdownTimers = new WeakMap();
  const PULSE_AT_SECONDS = 30;
  const STORAGE_PREFIX = 'wbs-cdt-end-';

  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function destroyCountdownBar(el) {
    const state = countdownTimers.get(el);
    if (!state) return;
    if (state.rafId) cancelAnimationFrame(state.rafId);
    countdownTimers.delete(el);
    el.removeAttribute('data-countdown-init');
  }

  function getEndTime(storageKey, totalSecs) {
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const endTime = parseInt(stored, 10);
        // Still valid if it hasn't expired
        if (endTime > Date.now()) return endTime;
      }
    } catch (_) {}

    // First visit or expired — start fresh
    const endTime = Date.now() + totalSecs * 1000;
    try {
      sessionStorage.setItem(storageKey, String(endTime));
    } catch (_) {}
    return endTime;
  }

  function saveEndTime(storageKey, endTime) {
    try {
      sessionStorage.setItem(storageKey, String(endTime));
    } catch (_) {}
  }

  function initCountdownBar(el) {
    if (!el || !el.isConnected) return;
    // Already running — skip
    if (el.hasAttribute('data-countdown-init')) return;

    const totalSecs = parseInt(el.dataset.countdown, 10) || 600;
    const showTimer = el.dataset.showTimer !== 'false';
    const animPulse = el.dataset.animatePulse !== 'false';
    const barText = el.dataset.barText || '';

    const minEl = el.querySelector('[data-role="minutes"]');
    const secEl = el.querySelector('[data-role="seconds"]');
    const timerEl = el.querySelector('.cb__timer');

    // Stable key based on element ID (which is now fixed in the liquid)
    const storageKey = STORAGE_PREFIX + (el.id || 'default');
    let endTime = getEndTime(storageKey, totalSecs);

    const state = { rafId: null };
    countdownTimers.set(el, state);
    el.setAttribute('data-countdown-init', '');

    function render(remaining) {
      if (!el.isConnected) {
        destroyCountdownBar(el);
        return;
      }

      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;

      if (showTimer && timerEl) {
        if (minEl) minEl.textContent = pad(minutes);
        if (secEl) secEl.textContent = pad(seconds);
      }

      if (animPulse) {
        el.classList.toggle('cb--ending', remaining <= PULSE_AT_SECONDS);
      }

      // Keep the label meaningful — include bar text so context is not lost
      const timeStr = minutes + ' minutes and ' + seconds + ' seconds remaining';
      el.setAttribute('aria-label', barText ? barText + ': ' + timeStr : timeStr);
    }

    function tick() {
      if (!el.isConnected) {
        destroyCountdownBar(el);
        return;
      }

      let remaining = Math.round((endTime - Date.now()) / 1000);

      if (remaining <= 0) {
        // Reset: start a new cycle
        endTime = Date.now() + totalSecs * 1000;
        saveEndTime(storageKey, endTime);
        el.classList.remove('cb--ending');
        remaining = totalSecs;
      }

      render(remaining);
      state.rafId = requestAnimationFrame(tick);
    }

    // Render the current (persisted) state immediately, then start loop
    render(Math.max(0, Math.round((endTime - Date.now()) / 1000)));
    state.rafId = requestAnimationFrame(tick);
  }

  function initCountdownBars(root) {
    const scope = (root && root.querySelectorAll) ? root : document;
    scope.querySelectorAll('.countdown-bar:not([data-countdown-init])').forEach(initCountdownBar);
  }

  function setupCartDrawerObserver() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.hasAttribute('data-countdown-observer')) return;

    drawer.setAttribute('data-countdown-observer', '');
    const observer = new MutationObserver(() => {
      // Only act when the timer bar itself is not yet initialized
      if (drawer.querySelector('.countdown-bar:not([data-countdown-init])')) {
        initCountdownBars(drawer);
      }
    });

    observer.observe(drawer, { childList: true, subtree: true });
  }

  function onReady() {
    initCountdownBars(document);
    setupCartDrawerObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  // Respond to Dawn pub/sub cartUpdate events if available
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      requestAnimationFrame(() => {
        initCountdownBars(document.querySelector('cart-drawer') || document);
      });
    });
  }

  // Theme editor cleanup
  document.addEventListener('shopify:section:unload', (event) => {
    document.querySelectorAll('.countdown-bar').forEach((el) => {
      if (el.dataset.sectionId === event.detail.sectionId) {
        destroyCountdownBar(el);
      }
    });
  });

  document.addEventListener('shopify:section:load', () => {
    initCountdownBars(document.querySelector('cart-drawer') || document);
  });

  window.WbsCountdownTimer = {
    init: initCountdownBars,
    destroy: destroyCountdownBar,
  };
})();
